/**
 * お気に入りの貸出ライフサイクル状態 (wishlist / reserved / returned) に対する
 * 共通メタ (ラベル・アイコン・カラーバリアント) をここに集約する。
 *
 * BookCard の状態遷移ボタンと LibrarySearchPage の状態フィルタチップで同じ真実を
 * 参照するため、色やラベルの食い違いを防ぐ。
 */

import { BookmarkCheck, CheckCircle2, type LucideIcon, Star } from 'lucide-react'

import type { LibraryFavoriteStatus } from '@/api/types/library-favorite'

export type FavoriteStatusMeta = {
  status: LibraryFavoriteStatus
  /** UI 共通ラベル (「候補 / 予約中 / 履歴」) */
  label: string
  icon: LucideIcon
  /** ボタンのアクティブ強調に使う text 系クラス (例: `text-amber-400`)。 */
  activeTextClass: string
  /** フィルタチップのアクティブ配色 (border + bg + text 一括)。 */
  activeChipClass: string
}

// ライフサイクル順 (候補 → 予約中 → 履歴)。UI の並び順に直接使うため配列で保持する。
export const FAVORITE_STATUS_META: readonly FavoriteStatusMeta[] = [
  {
    status: 'wishlist',
    label: '候補',
    icon: Star,
    activeTextClass: 'text-amber-400',
    activeChipClass: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
  },
  {
    status: 'reserved',
    label: '予約中',
    icon: BookmarkCheck,
    activeTextClass: 'text-emerald-400',
    activeChipClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
  },
  {
    status: 'returned',
    label: '履歴',
    icon: CheckCircle2,
    activeTextClass: 'text-sky-400',
    activeChipClass: 'bg-sky-500/20 text-sky-300 border-sky-500/50',
  },
]

// O(1) 参照用マップ。型アサーションを避けるため明示的に列挙する。
const META_BY_STATUS: Record<LibraryFavoriteStatus, FavoriteStatusMeta> = {
  wishlist: FAVORITE_STATUS_META[0],
  reserved: FAVORITE_STATUS_META[1],
  returned: FAVORITE_STATUS_META[2],
}

export const getFavoriteStatusMeta = (status: LibraryFavoriteStatus): FavoriteStatusMeta => META_BY_STATUS[status]
